import * as z from 'zod/mini'
import { keyedIssue } from '../errors/validation-keys'
import { EventVisibility, ReoccurrenceNth, ReoccurType, WeekDay } from '../types'
import { cancellationDeadlineField } from '../utils/cancellation-deadline'
import { activityFields } from './activity'

/**
 * Field rules for an event and an event blueprint, shared by the HTTP bodies and the domain
 * operations. `capacity` 0 is allowed in the domain (seed data, blueprints copied as they are); the
 * HTTP bodies require at least 1. `price` is in EUR; 0 means no price in the domain, and the HTTP
 * bodies require it to be positive when sent (docs/events.md, _Price_). `color` is the activity's
 * palette rule; an event without one shows its activity's colour (docs/events.md, _Colour_).
 * `description` is HTML from the manager's editor; the API sanitises it before storing it, and the
 * limit counts the markup too (docs/events.md, _Description_).
 */
export const eventFields = {
  name: z.string().check(z.trim(), z.minLength(2), z.maxLength(40)),
  capacity: z.int().check(z.nonnegative()),
  price: z.number().check(z.nonnegative(), z.maximum(99999)),
  visibility: z.array(z.enum(EventVisibility)),
  color: activityFields.color,
  description: z.string().check(z.maxLength(5000)),
  /** Minutes before the start; copied onto each sign-up (docs/events.md, _Cancelling a sign-up_). */
  cancellationDeadline: cancellationDeadlineField,
  /** The public sign-up offers "Poznámka pre prevádzku" (docs/events.md, _Client note_). */
  allowCustomerNote: z.boolean(),
  /** A blueprint's own name, for managers only; set when it is created and never edited (docs/events.md, _Series name_). */
  seriesName: z.string().check(z.trim(), z.minLength(2), z.maxLength(80)),
}

/**
 * How a blueprint repeats. It must end, by a date or after a number of occurrences, but not both
 * (`validateReoccurance` in business/misc/event-reoccurance.ts states the same rule for callers
 * without a schema). Weekly and monthly repeats name the weekdays; monthly also names the weeks.
 */
export const reoccurrenceFields = {
  reoccurType: z.enum(ReoccurType),
  reoccurNumOfTimes: z.optional(z.int().check(z.positive())),
  weekdays: z.optional(z.array(z.enum(WeekDay))),
  weeks: z.optional(z.array(z.enum(ReoccurrenceNth))),
}

export const reoccurrenceRule = <
  T extends {
    reoccurType: ReoccurType
    reoccurUntil?: string | Date
    reoccurNumOfTimes?: number
    weekdays?: WeekDay[]
    weeks?: ReoccurrenceNth[]
  },
>(
  value: T,
  ctx: z.core.$RefinementCtx<T>,
) => {
  if (!value.reoccurUntil === !value.reoccurNumOfTimes) {
    ctx.addIssue(keyedIssue('validation.reoccurEndRequired', ['reoccurUntil']))
  }
  const needsWeekdays = value.reoccurType === ReoccurType.WEEKLY || value.reoccurType === ReoccurType.MONTHLY
  if (needsWeekdays && !value.weekdays?.length) {
    ctx.addIssue(keyedIssue('validation.weekdaysRequired', ['weekdays']))
  }
  if (value.reoccurType === ReoccurType.MONTHLY && !value.weeks?.length) {
    ctx.addIssue(keyedIssue('validation.weeksRequired', ['weeks']))
  }
}
